"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import type { VehicleInfoData } from "./VehicleInfoForm";
import type { RegistrationDetailsData } from "./RegistrationDetailsForm";
import type { OwnerInfoData } from "./OwnerInfoForm";
import type { DocumentUploadData } from "./DocumentUploadForm";

interface ReviewSubmitFormProps {
  vehicleInfo: VehicleInfoData;
  registrationDetails: RegistrationDetailsData;
  ownerInfo: OwnerInfoData;
  documents: DocumentUploadData;
  onBack: () => void;
  onEdit: (step: number) => void;
}

export default function ReviewSubmitForm({
  vehicleInfo,
  registrationDetails,
  ownerInfo,
  documents,
  onBack,
  onEdit,
}: ReviewSubmitFormProps) {
  const router = useRouter();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      setError("Du må godta vilkårene og betingelsene");
      return;
    }

    setIsSubmitting(true);
    setError("");

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Redirect to dashboard on success
    router.push("/dashboard");
  };

  const handleSaveDraft = () => {
    // Simulate saving draft
    alert("Utkast lagret!");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Vehicle Information */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Kjøretøyinformasjon</h3>
          <button
            type="button"
            onClick={() => onEdit(0)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Icon name="PencilIcon" size={16} />
            Rediger
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground mb-1">VIN</p>
            <p className="font-mono font-semibold text-foreground">{vehicleInfo.vin}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Merke</p>
            <p className="font-semibold text-foreground">{vehicleInfo.make}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Modell</p>
            <p className="font-semibold text-foreground">{vehicleInfo.model}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">År</p>
            <p className="font-semibold text-foreground">{vehicleInfo.year}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Farge</p>
            <p className="font-semibold text-foreground">{vehicleInfo.color}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Registreringsnummer</p>
            <p className="font-mono font-semibold text-foreground">{vehicleInfo.licensePlate}</p>
          </div>
        </div>
      </div>

      {/* Registration Details */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Registreringsdetaljer</h3>
          <button
            type="button"
            onClick={() => onEdit(1)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Icon name="PencilIcon" size={16} />
            Rediger
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground mb-1">Registreringsnummer</p>
            <p className="font-mono font-semibold text-foreground">{registrationDetails.registrationNumber}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Utstedende myndighet</p>
            <p className="font-semibold text-foreground">{registrationDetails.issuingAuthority}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Registreringsdato</p>
            <p className="font-semibold text-foreground">
              {new Date(registrationDetails.registrationDate).toLocaleDateString("nb-NO", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Utløpsdato</p>
            <p className="font-semibold text-foreground">
              {new Date(registrationDetails.expiryDate).toLocaleDateString("nb-NO", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Fylke</p>
            <p className="font-semibold text-foreground">{registrationDetails.registrationState}</p>
          </div>
        </div>
      </div>

      {/* Owner Information */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Eierinformasjon</h3>
          <button
            type="button"
            onClick={() => onEdit(2)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Icon name="PencilIcon" size={16} />
            Rediger
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground mb-1">Fullt navn</p>
            <p className="font-semibold text-foreground">{ownerInfo.ownerName}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">E-post</p>
            <p className="font-semibold text-foreground">{ownerInfo.ownerEmail}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Telefon</p>
            <p className="font-semibold text-foreground">{ownerInfo.ownerPhone}</p>
          </div>
          <div className="col-span-2">
            <p className="text-muted-foreground mb-1">Adresse</p>
            <p className="font-semibold text-foreground">
              {ownerInfo.ownerAddress}, {ownerInfo.ownerCity}, {ownerInfo.ownerState} {ownerInfo.ownerZip}
            </p>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Opplastede dokumenter</h3>
          <button
            type="button"
            onClick={() => onEdit(3)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Icon name="PencilIcon" size={16} />
            Rediger
          </button>
        </div>
        <div className="space-y-3">
          {documents.registrationCertificate && (
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <Icon name="DocumentTextIcon" className="text-primary" size={24} />
              <div>
                <p className="font-semibold text-foreground text-sm">Registreringsbevis</p>
                <p className="text-xs text-muted-foreground">{documents.registrationCertificate.name}</p>
              </div>
            </div>
          )}
          {documents.insuranceCertificate && (
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <Icon name="DocumentTextIcon" className="text-primary" size={24} />
              <div>
                <p className="font-semibold text-foreground text-sm">Forsikringsbevis</p>
                <p className="text-xs text-muted-foreground">{documents.insuranceCertificate.name}</p>
              </div>
            </div>
          )}
          {documents.inspectionReport && (
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <Icon name="DocumentTextIcon" className="text-primary" size={24} />
              <div>
                <p className="font-semibold text-foreground text-sm">Inspeksjonsrapport</p>
                <p className="text-xs text-muted-foreground">{documents.inspectionReport.name}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="bg-muted/50 border border-border rounded-xl p-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => {
              setAgreedToTerms(e.target.checked);
              setError("");
            }}
            className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-ring"
          />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-2">
              Jeg godtar vilkårene og betingelsene *
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Ved å sende inn dette skjemaet bekrefter jeg at all informasjon som er oppgitt er nøyaktig og fullstendig. Jeg forstår at
              å oppgi falsk informasjon kan føre til avvisning av denne registreringen eller juridiske konsekvenser. Jeg godtar å
              motta fornyelsespåminnelser og viktige oppdateringer via e-post og SMS.
            </p>
          </div>
        </label>
        {error && (
          <p className="mt-3 text-sm text-error flex items-center gap-2">
            <Icon name="ExclamationCircleIcon" size={16} />
            {error}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 pt-4">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-8 py-3 bg-slate-100 text-foreground rounded-xl font-semibold hover:bg-slate-200 transition-all flex items-center gap-2"
          >
            <Icon name="ArrowLeftIcon" size={20} />
            Tilbake
          </button>
          <button
            type="button"
            onClick={handleSaveDraft}
            className="px-8 py-3 border-2 border-gray-300 text-gray-900 rounded-lg font-semibold hover:bg-gray-50 transition-all flex items-center gap-2"
          >
            <Icon name="BookmarkIcon" size={18} />
            Lagre som utkast
          </button>
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-10 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-900 transition-all shadow-clean flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Sender inn...
            </>
          ) : (
            <>
              <Icon name="CheckCircleIcon" size={20} />
              Registrer kjøretøy
            </>
          )}
        </button>
      </div>
    </form>
  );
}